import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type {
  Cliente, Opcion, Pagina, ReporteCargaSucursales, ResumenSucursales, Sucursal,
} from '../api/tipos';
import { useSesion } from '../sesion';
import { ESTADOS_DE_MEXICO, elEstadoQueEs } from '../estados-de-mexico';
import {
  Cargando, Dialogo, DialogoMotivo, Error, Insignia, Paginacion,
} from '../componentes/comunes';

/**
 * Clientes y sucursales.
 *
 * Se ven juntos porque el trabajo diario es sobre las sucursales: Walmart
 * de México trae 365 tiendas y lo que se consulta es «qué tiendas hay en
 * el Bajío». Pero el catálogo de clientes es el que prellena la
 * prefactura, y ahí entra todo aquel a quien haya que facturarle, tenga
 * sucursales o no.
 */
export default function Clientes() {
  // Abre en clientes y no en sucursales. Se llamaban «cadenas», que es
  // acertado para Walmart y esconde el catálogo de quien busca darle de
  // alta a alguien más: quien quiere facturarle a un cliente nuevo busca
  // clientes, no cadenas.
  const [vista, setVista] = useState<'clientes' | 'sucursales'>('clientes');

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Clientes</h1>
          <p className="sub">
            A quién se le factura, y sus sucursales. Los datos fiscales del
            cliente son los que prellenan la prefactura. Cada sucursal
            pertenece a un estado y el estado a una región: eso determina
            qué ve cada supervisor y qué técnicos se ofrecen al asignar.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={vista === 'clientes' ? 'primario' : ''}
            onClick={() => setVista('clientes')}
          >
            Clientes
          </button>
          <button
            className={vista === 'sucursales' ? 'primario' : ''}
            onClick={() => setVista('sucursales')}
          >
            Sucursales
          </button>
        </div>
      </div>

      {vista === 'sucursales' ? <Sucursales /> : <CatalogoDeClientes />}
    </>
  );
}

// ══════════════════════════════════════════════════════════ sucursales

function Sucursales() {
  const { puede } = useSesion();

  const [busqueda, setBusqueda] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [regionId, setRegionId] = useState('');
  const [estadoId, setEstadoId] = useState('');
  const [tipoIgualaId, setTipoIgualaId] = useState('');
  const [conDireccion, setConDireccion] = useState('');
  const [estado, setEstado] = useState('');
  const [numero, setNumero] = useState(1);

  const [pagina, setPagina] = useState<Pagina<Sucursal> | null>(null);
  const [resumen, setResumen] = useState<ResumenSucursales | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [clientes, setClientes] = useState<Opcion[]>([]);
  const [regiones, setRegiones] = useState<Opcion[]>([]);
  const [estados, setEstados] = useState<Opcion[]>([]);
  const [igualas, setIgualas] = useState<Opcion[]>([]);
  const [formatos, setFormatos] = useState<Opcion[]>([]);

  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<Sucursal | null>(null);
  const [viendo, setViendo] = useState<Sucursal | null>(null);
  const [cambiandoEstado, setCambiandoEstado] = useState<Sucursal | null>(null);
  const [cargandoArchivo, setCargandoArchivo] = useState(false);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/regiones/opciones').then(setRegiones).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/tipos-iguala/opciones').then(setIgualas).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/formatos-tienda/opciones')
      .then(setFormatos)
      .catch(() => undefined);
    api.get<Pagina<Cliente>>('/clientes', { limite: 200, estado: 'activo' })
      .then((r) => setClientes(r.data.map((c) => ({ id: c.id, nombre: c.nombre }))))
      .catch(() => undefined);
  }, []);

  // Los estados se acotan a la región elegida: son 40 y ofrecerlos todos
  // cuando ya se filtró por región no ayuda.
  useEffect(() => {
    api.get<Opcion[]>('/catalogos/estados/opciones', {
      region_id: regionId || undefined,
    })
      .then(setEstados)
      .catch(() => setEstados([]));
  }, [regionId]);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Sucursal>>('/sucursales', {
      busqueda: busqueda || undefined,
      clienteId: clienteId || undefined,
      regionId: regionId || undefined,
      estadoId: estadoId || undefined,
      tipoIgualaId: tipoIgualaId || undefined,
      conDireccion: conDireccion || undefined,
      estado: estado || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));

    api.get<ResumenSucursales>('/sucursales/resumen').then(setResumen).catch(() => undefined);
  }, [busqueda, clienteId, regionId, estadoId, tipoIgualaId, conDireccion, estado, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  return (
    <div className="panel">
      {resumen && (
        <div className="aviso" style={{ marginBottom: 14 }}>
          {resumen.total} sucursales activas en tu ámbito.
          {resumen.sinDireccion > 0 && (
            <> <strong>{resumen.sinDireccion} sin domicilio</strong>: el técnico
            no sabe a dónde ir. Es el dato que sigue pendiente de APRISA.</>
          )}
          {resumen.sinIguala > 0 && <> {resumen.sinIguala} sin tipo de iguala.</>}
          {/* Sin formato no hay tarifa de iguala que buscar, y la
              prefactura se queda sin precio. Son ocho que el nombre no
              alcanzó a clasificar. */}
          {resumen.sinFormato > 0 && (
            <> {resumen.sinFormato} sin formato de tienda: su iguala no
            encuentra tarifa.</>
          )}
        </div>
      )}

      <div className="controles">
        <input
          type="search"
          placeholder="Determinante, nombre, ciudad o dirección…"
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
        />
        {clientes.length > 1 && (
          <select
            value={clienteId}
            onChange={(e) => { setClienteId(e.target.value); setNumero(1); }}
          >
            <option value="">Todos los clientes</option>
            {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        )}
        <select
          value={regionId}
          onChange={(e) => { setRegionId(e.target.value); setEstadoId(''); setNumero(1); }}
        >
          <option value="">Todas las regiones</option>
          {regiones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
        </select>
        <select
          value={estadoId}
          onChange={(e) => { setEstadoId(e.target.value); setNumero(1); }}
        >
          <option value="">{regionId ? 'Todos los estados de la región' : 'Todos los estados'}</option>
          {estados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
        </select>
        <select
          value={tipoIgualaId}
          onChange={(e) => { setTipoIgualaId(e.target.value); setNumero(1); }}
        >
          <option value="">Todas las igualas</option>
          {igualas.map((i) => <option key={i.id} value={i.id}>{i.nombre}</option>)}
        </select>
        <select
          value={conDireccion}
          onChange={(e) => { setConDireccion(e.target.value); setNumero(1); }}
        >
          <option value="">Con y sin domicilio</option>
          <option value="si">Con domicilio</option>
          <option value="no">Sin domicilio</option>
        </select>
        <select value={estado} onChange={(e) => { setEstado(e.target.value); setNumero(1); }}>
          <option value="">Todas</option>
          <option value="activo">Sólo activas</option>
          <option value="inactivo">Sólo inactivas</option>
        </select>
        {puede('clientes', 'crear') && (
          <>
            <button className="primario" onClick={() => setCreando(true)}>Nueva sucursal</button>
            <button onClick={() => setCargandoArchivo(true)}>Cargar de un archivo</button>
          </>
        )}
      </div>

      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">No hay sucursales que cumplan esos filtros.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Determinante</th>
                  <th>Nombre</th>
                  <th>Estado</th>
                  <th>Región</th>
                  <th>Iguala</th>
                  <th>Domicilio</th>
                  <th>Estatus</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((s) => (
                  <tr key={s.id}>
                    <td className="mono">{s.determinante}</td>
                    <td>
                      <button className="enlace" onClick={() => setViendo(s)}>{s.nombre}</button>
                    </td>
                    <td>{s.estado?.nombre ?? '—'}</td>
                    <td>{s.region?.nombre ?? '—'}</td>
                    <td>{s.tipoIguala?.nombre ?? '—'}</td>
                    <td>
                      {s.direccion
                        ? [s.direccion, s.ciudad].filter(Boolean).join(', ')
                        : <span className="etiqueta no">Falta</span>}
                    </td>
                    <td><Insignia activo={s.activo} /></td>
                    <td className="acciones">
                      {puede('clientes', 'editar') && (
                        <>
                          <button className="enlace" onClick={() => setEditando(s)}>Editar</button>
                          <button className="enlace" onClick={() => setCambiandoEstado(s)}>
                            {s.activo ? 'Desactivar' : 'Reactivar'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina}
            limite={pagina.limite}
            total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {viendo && <FichaSucursal sucursal={viendo} onCerrar={() => setViendo(null)} />}

      {(creando || editando) && (
        <FormularioSucursal
          clientes={clientes}
          regiones={regiones}
          igualas={igualas}
          formatos={formatos}
          sucursal={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
          onGuardado={() => { setCreando(false); setEditando(null); cargar(); }}
        />
      )}

      {cambiandoEstado && (
        <CambioEstadoSucursal
          sucursal={cambiandoEstado}
          onCerrar={() => setCambiandoEstado(null)}
          onHecho={() => { setCambiandoEstado(null); cargar(); }}
        />
      )}

      {cargandoArchivo && (
        <CargarSucursales
          clientes={clientes}
          clienteElegido={clienteId}
          onCerrar={() => setCargandoArchivo(false)}
          onHecho={() => { setCargandoArchivo(false); cargar(); }}
        />
      )}
    </div>
  );
}

/** Las columnas que el archivo puede traer, para enseñarlas antes de subir. */
const COLUMNAS_DEL_ARCHIVO: { nombre: string; nota: string }[] = [
  { nombre: 'determinante', nota: 'Obligatoria. Es la identidad de la tienda.' },
  { nombre: 'nombre', nota: 'Obligatoria en las nuevas.' },
  { nombre: 'estado', nota: 'Obligatoria en las nuevas. De aquí sale la zona.' },
  { nombre: 'iguala', nota: 'Decide la periodicidad del preventivo.' },
  { nombre: 'formato', nota: 'Con el área decide si el trabajo se cobra.' },
  { nombre: 'direccion, colonia, ciudad, cp', nota: 'El domicilio.' },
  { nombre: 'contacto, telefono, correo', nota: 'Quién recibe en la tienda.' },
  { nombre: 'latitud, longitud', nota: 'Opcionales.' },
  { nombre: 'mes ancla', nota: 'Sólo trimestrales. Admite «2» o «febrero».' },
];

/**
 * Cargar el catálogo de un archivo.
 *
 * Dos pasadas: primero se enseña qué va a pasar con cada renglón y sólo
 * cuando alguien lo aprueba se escribe. Un catálogo de 365 tiendas no se
 * corrige a ciegas, y quien sube el archivo casi nunca lo hizo él.
 */
function CargarSucursales({ clientes, clienteElegido, onCerrar, onHecho }: {
  clientes: Opcion[];
  /** El del filtro de arriba, si había uno. Ahorra elegirlo dos veces. */
  clienteElegido: string;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [clienteId, setClienteId] = useState(
    clienteElegido || (clientes.length === 1 ? clientes[0].id : ''),
  );
  const [archivo, setArchivo] = useState<File | null>(null);
  const [reporte, setReporte] = useState<ReporteCargaSucursales | null>(null);
  /** La hoja del libro, cuando trae más de una con sucursales. */
  const [hoja, setHoja] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  const yaEscrito = reporte !== null && !reporte.simulacion;

  async function mandar(simular: boolean, hojaPedida = hoja) {
    if (!archivo || !clienteId) return;
    setError(null);
    setTrabajando(true);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo);
      setReporte(await api.subir<ReporteCargaSucursales>(
        '/sucursales/carga',
        cuerpo,
        { clienteId, simular: simular ? 'si' : undefined, hoja: hojaPedida || undefined },
      ));
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo titulo="Cargar sucursales de un archivo" onCerrar={onCerrar} ancho>
      <Error error={error} />

      {!yaEscrito && (
        <>
          <div className="campo">
            <label htmlFor="clienteCarga">Cliente *</label>
            <select
              id="clienteCarga"
              value={clienteId}
              onChange={(e) => { setClienteId(e.target.value); setReporte(null); }}
            >
              <option value="">Elige el cliente…</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
            <span className="ayuda">
              La determinante es única dentro del cliente, no en todo el
              sistema: dos cadenas pueden usar el mismo número.
            </span>
          </div>

          <div className="campo">
            <label htmlFor="archivoSucursales">Archivo</label>
            <input
              id="archivoSucursales"
              type="file"
              accept=".csv,.xlsx,.xls,text/csv"
              onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); setReporte(null); }}
            />
            <span className="ayuda">Excel o CSV. Hasta 5 MB.</span>
          </div>

          <details style={{ margin: '8px 0 12px' }}>
            <summary>Qué columnas puede traer</summary>
            <table style={{ marginTop: 8 }}>
              <tbody>
                {COLUMNAS_DEL_ARCHIVO.map((c) => (
                  <tr key={c.nombre}>
                    <td className="mono">{c.nombre}</td>
                    <td className="ayuda">{c.nota}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="ayuda">
              Los encabezados se reconocen con sus variantes normales —«no.
              tienda» vale por determinante, «domicilio» por dirección—, y
              las columnas que no se reconozcan se ignoran y se dicen.
              El estado, la iguala y el formato van por nombre.
            </p>
          </details>

          {/*
            La regla que hay que decir antes de subir, no después.

            Es lo que separa un archivo de alta de uno de corrección, y
            quien lo sube tiene derecho a saber que su lista de cinco
            tiendas nuevas no va a dejar sin domicilio a las demás.
          */}
          <div className="ayuda" style={{ marginBottom: 12 }}>
            Las determinantes que ya existan se actualizan con lo que traiga
            el archivo. <strong>Una celda vacía no borra lo que ya estaba</strong>:
            sólo se escribe lo que el renglón trae escrito.
          </div>
        </>
      )}

      {/*
        Qué hoja se leyó, y las otras que también servían.

        Un libro de trabajo trae diez hojas y a veces dos listas de
        tiendas —la vieja y la nueva—. Quedarse con la primera sin decirlo
        cargaría la que no es, y encima con el mismo aspecto de éxito.
      */}
      {reporte?.hoja && !yaEscrito && (
        <div className={reporte.hojasCandidatas.length > 1 ? 'aviso' : 'ayuda'}>
          Se leyó la hoja <strong>{reporte.hoja}</strong>.
          {reporte.hojasCandidatas.length > 1 && (
            <div className="campo" style={{ marginTop: 8 }}>
              <label htmlFor="hojaDelLibro">
                El libro trae otras hojas con sucursales. ¿Es ésta?
              </label>
              <select
                id="hojaDelLibro"
                value={hoja || reporte.hoja}
                disabled={trabajando}
                onChange={(e) => { setHoja(e.target.value); void mandar(true, e.target.value); }}
              >
                {reporte.hojasCandidatas.map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {reporte && <ResumenCarga reporte={reporte} />}

      <div className="controles">
        {!reporte && (
          <button
            className="primario"
            disabled={!archivo || !clienteId || trabajando}
            onClick={() => { void mandar(true); }}
          >
            Revisar el archivo
          </button>
        )}

        {reporte?.simulacion && (
          <>
            <button
              className="primario"
              disabled={trabajando
                || (reporte.nuevas.length === 0 && reporte.actualizadas.length === 0)}
              onClick={() => { void mandar(false); }}
            >
              Cargar {reporte.nuevas.length + reporte.actualizadas.length} sucursal(es)
            </button>
            <button disabled={trabajando} onClick={() => { setReporte(null); setArchivo(null); }}>
              Elegir otro archivo
            </button>
          </>
        )}

        {yaEscrito && <button className="primario" onClick={onHecho}>Listo</button>}
      </div>

      {/* Un botón desactivado tiene que decir qué le falta. */}
      {!reporte && (!archivo || !clienteId) && (
        <span className="ayuda">
          {!clienteId ? 'Elige el cliente' : 'Elige el archivo'} para poder revisarlo.
        </span>
      )}
    </Dialogo>
  );
}

/** Qué va a pasar —o qué pasó— con el archivo. */
function ResumenCarga({ reporte }: { reporte: ReporteCargaSucursales }) {
  return (
    <>
      <p>
        {reporte.simulacion ? 'Del archivo se pueden cargar' : 'Se cargaron'}{' '}
        <strong>{reporte.nuevas.length}</strong> sucursal(es) nueva(s) y{' '}
        <strong>{reporte.actualizadas.length}</strong> se
        {reporte.simulacion ? ' actualizarían' : ' actualizaron'}, de{' '}
        {reporte.total} renglones.
      </p>

      {reporte.filaEncabezado > 1 && (
        <p className="ayuda">
          El encabezado se leyó del renglón {reporte.filaEncabezado}; los
          anteriores venían vacíos.
        </p>
      )}

      {reporte.columnasIgnoradas.length > 0 && (
        <p className="ayuda">
          Columnas que no se reconocieron y no se usaron:{' '}
          {reporte.columnasIgnoradas.join(', ')}. Si alguna de ésas importaba,
          renómbrala y vuelve a subir el archivo.
        </p>
      )}

      {reporte.rechazados.length > 0 && (
        <>
          <h3>Renglones que no entran</h3>
          <table>
            <thead>
              <tr><th>Renglón</th><th>Determinante</th><th>Por qué</th></tr>
            </thead>
            <tbody>
              {reporte.rechazados.map((r) => (
                <tr key={`${r.numero}-${r.determinante ?? ''}`}>
                  <td className="numero">{r.numero}</td>
                  <td className="mono">{r.determinante ?? '—'}</td>
                  <td>{r.motivo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/*
        Lo que entra pero queda coja.

        Sin estado no tiene zona, sin iguala no entra al preventivo y sin
        formato no se puede decidir si el trabajo se cobra. Se carga igual
        —es mejor tenerla y completarla— pero se dice ahora, que es cuando
        alguien puede corregir el archivo.
      */}
      {reporte.incompletas.length > 0 && (
        <>
          <h3>Entran, pero les falta un dato</h3>
          <table>
            <thead>
              <tr><th>Determinante</th><th>Qué le falta</th></tr>
            </thead>
            <tbody>
              {reporte.incompletas.map((i) => (
                <tr key={i.determinante}>
                  <td className="mono">{i.determinante}</td>
                  <td>{i.falta.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {reporte.avisos.length > 0 && (
        <>
          <h3>Avisos</h3>
          <p className="ayuda">
            Estos renglones sí entran. Se dice lo que se corrigió o lo que se
            dejó sin poner.
          </p>
          <table>
            <thead>
              <tr><th>Renglón</th><th>Determinante</th><th>Aviso</th></tr>
            </thead>
            <tbody>
              {reporte.avisos.map((a, i) => (
                <tr key={`${a.renglon}-${i}`}>
                  <td className="numero">{a.renglon}</td>
                  <td className="mono">{a.determinante}</td>
                  <td>{a.texto}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {reporte.nuevas.length > 0 && (
        <>
          <h3>Sucursales nuevas</h3>
          <table>
            <thead>
              <tr><th>Renglón</th><th>Determinante</th><th>Nombre</th></tr>
            </thead>
            <tbody>
              {reporte.nuevas.map((n) => (
                <tr key={n.determinante}>
                  <td className="numero">{n.renglon}</td>
                  <td className="mono">{n.determinante}</td>
                  <td>{n.nombre ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {reporte.actualizadas.length > 0 && (
        <>
          <h3>Sucursales que se actualizan</h3>
          <table>
            <thead>
              <tr><th>Renglón</th><th>Determinante</th><th>Qué cambia</th></tr>
            </thead>
            <tbody>
              {reporte.actualizadas.map((a) => (
                <tr key={a.determinante}>
                  <td className="numero">{a.renglon}</td>
                  <td className="mono">{a.determinante}</td>
                  <td className="ayuda">{a.campos.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function FichaSucursal({ sucursal, onCerrar }: { sucursal: Sucursal; onCerrar: () => void }) {
  const domicilio = [sucursal.direccion, sucursal.colonia, sucursal.ciudad, sucursal.codigoPostal]
    .filter(Boolean).join(', ');

  return (
    <Dialogo
      titulo={`${sucursal.determinante} — ${sucursal.nombre}`}
      onCerrar={onCerrar}
      pie={<button onClick={onCerrar}>Cerrar</button>}
    >
      <Dato etiqueta="Cliente" valor={sucursal.cliente?.nombre ?? null} />
      <Dato etiqueta="Estado" valor={sucursal.estado?.nombre ?? null} />
      <Dato etiqueta="Región" valor={sucursal.region?.nombre ?? null} />
      <Dato etiqueta="Tipo de iguala" valor={sucursal.tipoIguala?.nombre ?? null} />
      <Dato etiqueta="Formato de tienda" valor={sucursal.formatoTienda?.nombre ?? null} />

      <h2>Domicilio</h2>
      {domicilio ? <Dato etiqueta="Dirección" valor={domicilio} /> : (
        <div className="aviso">
          Sin domicilio capturado. El técnico no sabe a dónde ir.
        </div>
      )}
      {sucursal.latitud !== null && sucursal.longitud !== null && (
        <Dato
          etiqueta="Coordenadas"
          valor={`${sucursal.latitud}, ${sucursal.longitud}`}
          mono
        />
      )}

      <h2>Contacto en sitio</h2>
      <Dato etiqueta="Contacto" valor={sucursal.contactoSitio} />
      <Dato etiqueta="Teléfono" valor={sucursal.telefonoSitio} />
      <Dato etiqueta="Correo" valor={sucursal.correoSitio} />
    </Dialogo>
  );
}

function FormularioSucursal({
  clientes, regiones, igualas, formatos, sucursal, onCerrar, onGuardado,
}: {
  clientes: Opcion[];
  regiones: Opcion[];
  igualas: Opcion[];
  formatos: Opcion[];
  sucursal: Sucursal | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const esAlta = sucursal === null;

  const [regionId, setRegionId] = useState(sucursal?.region?.id ?? '');
  const [estados, setEstados] = useState<Opcion[]>([]);
  const [datos, setDatos] = useState({
    clienteId: sucursal?.cliente?.id ?? clientes[0]?.id ?? '',
    estadoId: sucursal?.estado?.id ?? '',
    tipoIgualaId: sucursal?.tipoIguala?.id ?? '',
    formatoTiendaId: sucursal?.formatoTienda?.id ?? '',
    determinante: sucursal?.determinante ?? '',
    nombre: sucursal?.nombre ?? '',
    direccion: sucursal?.direccion ?? '',
    colonia: sucursal?.colonia ?? '',
    ciudad: sucursal?.ciudad ?? '',
    codigoPostal: sucursal?.codigoPostal ?? '',
    contactoSitio: sucursal?.contactoSitio ?? '',
    telefonoSitio: sucursal?.telefonoSitio ?? '',
    correoSitio: sucursal?.correoSitio ?? '',
  });
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/estados/opciones', { region_id: regionId || undefined })
      .then(setEstados)
      .catch(() => setEstados([]));
  }, [regionId]);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      const cuerpo: Record<string, unknown> = {
        estadoId: datos.estadoId,
        tipoIgualaId: datos.tipoIgualaId || undefined,
        formatoTiendaId: datos.formatoTiendaId || undefined,
        determinante: datos.determinante.trim(),
        nombre: datos.nombre.trim(),
        direccion: datos.direccion.trim() || undefined,
        colonia: datos.colonia.trim() || undefined,
        ciudad: datos.ciudad.trim() || undefined,
        codigoPostal: datos.codigoPostal.trim() || undefined,
        contactoSitio: datos.contactoSitio.trim() || undefined,
        telefonoSitio: datos.telefonoSitio.trim() || undefined,
        correoSitio: datos.correoSitio.trim() || undefined,
      };

      if (esAlta) {
        cuerpo.clienteId = datos.clienteId;
        await api.post('/sucursales', cuerpo);
      } else {
        await api.patch(`/sucursales/${sucursal!.id}`, cuerpo);
      }
      onGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={esAlta ? 'Nueva sucursal' : `Editar ${sucursal!.determinante}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button
            className="primario"
            onClick={guardar}
            disabled={trabajando || !datos.estadoId || !datos.determinante || !datos.nombre}
          >
            {trabajando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      {esAlta && clientes.length > 1 && (
        <div className="campo">
          <label htmlFor="clienteId">Cliente *</label>
          <select
            id="clienteId" value={datos.clienteId}
            onChange={(e) => setDatos({ ...datos, clienteId: e.target.value })}
          >
            {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
      )}

      <div className="campo">
        <label htmlFor="determinante">Determinante *</label>
        <input
          id="determinante" className="mono" value={datos.determinante} maxLength={20}
          onChange={(e) => setDatos({ ...datos, determinante: e.target.value })}
        />
        <span className="ayuda">
          El número con el que el cliente identifica la tienda. Es único
          dentro del cliente, no globalmente.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="nombre">Nombre *</label>
        <input
          id="nombre" value={datos.nombre} maxLength={200}
          onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="region">Región *</label>
        <select
          id="region" value={regionId}
          onChange={(e) => { setRegionId(e.target.value); setDatos({ ...datos, estadoId: '' }); }}
        >
          <option value="">— elige una región —</option>
          {regiones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
        </select>
        <span className="ayuda">
          Sólo sirve para acotar los estados: la región se deduce del estado.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="estadoId">Estado *</label>
        <select
          id="estadoId" value={datos.estadoId}
          onChange={(e) => setDatos({ ...datos, estadoId: e.target.value })}
        >
          <option value="">— elige un estado —</option>
          {estados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
        </select>
      </div>

      <div className="campo">
        <label htmlFor="tipoIgualaId">Tipo de iguala</label>
        <select
          id="tipoIgualaId" value={datos.tipoIgualaId}
          onChange={(e) => setDatos({ ...datos, tipoIgualaId: e.target.value })}
        >
          <option value="">— sin asignar —</option>
          {igualas.map((i) => <option key={i.id} value={i.id}>{i.nombre}</option>)}
        </select>
        <span className="ayuda">Determina qué se cobra y qué entra en el servicio.</span>
      </div>

      <div className="campo">
        <label htmlFor="formatoTiendaId">Formato de tienda</label>
        <select
          id="formatoTiendaId" value={datos.formatoTiendaId}
          onChange={(e) => setDatos({ ...datos, formatoTiendaId: e.target.value })}
        >
          <option value="">— sin asignar —</option>
          {formatos.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
        </select>
        <span className="ayuda">
          Con el tipo de iguala determina el precio: la tarifa se pacta por
          la combinación de los dos. Sin formato, la prefactura de iguala se
          queda sin precio.
        </span>
      </div>

      <h2>Domicilio</h2>

      <div className="campo">
        <label htmlFor="direccion">Calle y número</label>
        <input
          id="direccion" value={datos.direccion} maxLength={250}
          onChange={(e) => setDatos({ ...datos, direccion: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="colonia">Colonia</label>
        <input
          id="colonia" value={datos.colonia} maxLength={120}
          onChange={(e) => setDatos({ ...datos, colonia: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="ciudad">Ciudad</label>
        <input
          id="ciudad" value={datos.ciudad} maxLength={120}
          onChange={(e) => setDatos({ ...datos, ciudad: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="codigoPostal">Código postal</label>
        <input
          id="codigoPostal" className="mono" value={datos.codigoPostal}
          maxLength={5} inputMode="numeric"
          onChange={(e) => setDatos({ ...datos, codigoPostal: e.target.value })}
        />
        <span className="ayuda">Cinco dígitos.</span>
      </div>

      <h2>Contacto en sitio</h2>

      <div className="campo">
        <label htmlFor="contactoSitio">Nombre</label>
        <input
          id="contactoSitio" value={datos.contactoSitio} maxLength={150}
          onChange={(e) => setDatos({ ...datos, contactoSitio: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="telefonoSitio">Teléfono</label>
        <input
          id="telefonoSitio" value={datos.telefonoSitio} maxLength={30}
          onChange={(e) => setDatos({ ...datos, telefonoSitio: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="correoSitio">Correo</label>
        <input
          id="correoSitio" type="email" value={datos.correoSitio} maxLength={150}
          onChange={(e) => setDatos({ ...datos, correoSitio: e.target.value })}
        />
      </div>
    </Dialogo>
  );
}

function CambioEstadoSucursal({
  sucursal, onCerrar, onHecho,
}: {
  sucursal: Sucursal;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      const accion = sucursal.activo ? 'desactivar' : 'reactivar';
      await api.post(`/sucursales/${sucursal.id}/${accion}`, { motivo });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <DialogoMotivo
      titulo={`${sucursal.activo ? 'Desactivar' : 'Reactivar'} sucursal`}
      textoBoton={sucursal.activo ? 'Desactivar' : 'Reactivar'}
      motivo={motivo}
      setMotivo={setMotivo}
      trabajando={trabajando}
      error={error}
      onConfirmar={confirmar}
      onCerrar={onCerrar}
      descripcion={
        <p>
          <strong>{sucursal.determinante} — {sucursal.nombre}</strong>
          {sucursal.activo
            ? ' dejará de ofrecerse al crear órdenes. Las que ya existen no cambian.'
            : ' volverá a estar disponible.'}
        </p>
      }
    />
  );
}

// ═══════════════════════════════════════════════════════════ clientes

function CatalogoDeClientes() {
  const { puede } = useSesion();

  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState<Pagina<Cliente> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [numero, setNumero] = useState(1);

  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<Cliente | null>(null);
  const [cambiandoEstado, setCambiandoEstado] = useState<Cliente | null>(null);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Cliente>>('/clientes', {
      busqueda: busqueda || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  return (
    <div className="panel">
      <div className="controles">
        <input
          type="search"
          placeholder="Código, nombre, razón social o RFC…"
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
        />
        {puede('clientes', 'crear') && (
          <button className="primario" onClick={() => setCreando(true)}>Nuevo cliente</button>
        )}
      </div>

      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">No hay clientes que cumplan esa búsqueda.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Razón social</th>
                  <th>RFC</th>
                  <th className="numero">Sucursales</th>
                  <th className="numero">Crédito</th>
                  <th>Estatus</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((c) => (
                  <tr key={c.id}>
                    <td className="mono">{c.codigo}</td>
                    <td>{c.nombre}</td>
                    <td>{c.razonSocial ?? '—'}</td>
                    <td className="mono">{c.rfc ?? '—'}</td>
                    <td className="numero">{c.sucursales ?? 0}</td>
                    <td className="numero">
                      {c.diasCredito === null ? '—' : `${c.diasCredito} d`}
                    </td>
                    <td><Insignia activo={c.activo} /></td>
                    <td className="acciones">
                      {puede('clientes', 'editar') && (
                        <>
                          <button className="enlace" onClick={() => setEditando(c)}>Editar</button>
                          <button className="enlace" onClick={() => setCambiandoEstado(c)}>
                            {c.activo ? 'Desactivar' : 'Reactivar'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina}
            limite={pagina.limite}
            total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {(creando || editando) && (
        <FormularioCliente
          cliente={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
          onGuardado={() => { setCreando(false); setEditando(null); cargar(); }}
        />
      )}

      {cambiandoEstado && (
        <CambioEstadoCliente
          cliente={cambiandoEstado}
          onCerrar={() => setCambiandoEstado(null)}
          onHecho={() => { setCambiandoEstado(null); cargar(); }}
        />
      )}
    </div>
  );
}

function FormularioCliente({
  cliente, onCerrar, onGuardado,
}: {
  cliente: Cliente | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const esAlta = cliente === null;
  const [datos, setDatos] = useState({
    codigo: cliente?.codigo ?? '',
    nombre: cliente?.nombre ?? '',
    razonSocial: cliente?.razonSocial ?? '',
    rfc: cliente?.rfc ?? '',
    regimenFiscal: cliente?.regimenFiscal ?? '',
    contactoPrincipal: cliente?.contactoPrincipal ?? '',
    correo: cliente?.correo ?? '',
    telefono: cliente?.telefono ?? '',
    direccionFacturacion: cliente?.direccionFacturacion ?? '',
    numeroExterior: cliente?.numeroExterior ?? '',
    numeroInterior: cliente?.numeroInterior ?? '',
    colonia: cliente?.colonia ?? '',
    ciudad: cliente?.ciudad ?? '',
    estadoTexto: cliente?.estadoTexto ?? '',
    codigoPostal: cliente?.codigoPostal ?? '',
    diasCredito: cliente?.diasCredito === null ? '' : String(cliente?.diasCredito ?? ''),
    // El alta arranca con lo habitual en servicios de mantenimiento a
    // crédito. Son sugerencias, no automatismos: se ven en pantalla y se
    // cambian. Poner nada obligaría a teclear cinco claves del SAT que
    // son iguales en casi todos los clientes; ponerlas en silencio sería
    // decidir por quien factura.
    usoCfdi: cliente?.usoCfdi ?? 'G03',
    formaPago: cliente?.formaPago ?? '99',
    formaPagoTexto: cliente?.formaPagoTexto ?? 'Por Definir',
    metodoPago: cliente?.metodoPago ?? 'PPD',
    metodoPagoEdi: cliente?.metodoPagoEdi ?? '',
    condicionesPago: cliente?.condicionesPago ?? 'CREDITO',
    numeroProveedor: cliente?.numeroProveedor ?? '',
    moneda: cliente?.moneda ?? 'MXN',
  });
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      const cuerpo: Record<string, unknown> = {
        nombre: datos.nombre.trim(),
        razonSocial: datos.razonSocial.trim() || undefined,
        rfc: datos.rfc.trim() || undefined,
        regimenFiscal: datos.regimenFiscal.trim() || undefined,
        contactoPrincipal: datos.contactoPrincipal.trim() || undefined,
        correo: datos.correo.trim() || undefined,
        telefono: datos.telefono.trim() || undefined,
        direccionFacturacion: datos.direccionFacturacion.trim() || undefined,
        numeroExterior: datos.numeroExterior.trim() || undefined,
        numeroInterior: datos.numeroInterior.trim() || undefined,
        colonia: datos.colonia.trim() || undefined,
        ciudad: datos.ciudad.trim() || undefined,
        estadoTexto: datos.estadoTexto.trim() || undefined,
        codigoPostal: datos.codigoPostal.trim() || undefined,
        diasCredito: datos.diasCredito === '' ? undefined : Number(datos.diasCredito),
        usoCfdi: datos.usoCfdi.trim() || undefined,
        formaPago: datos.formaPago.trim() || undefined,
        formaPagoTexto: datos.formaPagoTexto.trim() || undefined,
        metodoPago: datos.metodoPago || undefined,
        metodoPagoEdi: datos.metodoPagoEdi.trim() || undefined,
        condicionesPago: datos.condicionesPago.trim() || undefined,
        numeroProveedor: datos.numeroProveedor.trim() || undefined,
        moneda: datos.moneda.trim() || undefined,
      };
      if (esAlta) cuerpo.codigo = datos.codigo.trim();

      if (esAlta) await api.post('/clientes', cuerpo);
      else await api.patch(`/clientes/${cliente!.id}`, cuerpo);

      onGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={esAlta ? 'Nuevo cliente' : `Editar ${cliente!.codigo}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando}>
            {trabajando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      {esAlta && (
        <div className="campo">
          <label htmlFor="codigo">Código *</label>
          <input
            id="codigo" className="mono" value={datos.codigo} maxLength={20}
            onChange={(e) => setDatos({ ...datos, codigo: e.target.value })}
          />
        </div>
      )}

      <div className="campo">
        <label htmlFor="nombre">Nombre *</label>
        <input
          id="nombre" value={datos.nombre} maxLength={200}
          onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
        />
      </div>

      <h2>Datos fiscales</h2>
      <div className="aviso">
        Son los que prellenan la prefactura y los que se timbran. La
        factura los copia al emitirse, así que corregirlos aquí no cambia
        las que ya se emitieron.
      </div>

      <div className="campo">
        <label htmlFor="razonSocial">Razón social</label>
        <input
          id="razonSocial" value={datos.razonSocial} maxLength={250}
          onChange={(e) => setDatos({ ...datos, razonSocial: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="rfc">RFC</label>
        <input
          id="rfc" className="mono" value={datos.rfc} maxLength={13}
          onChange={(e) => setDatos({ ...datos, rfc: e.target.value.toUpperCase() })}
        />
      </div>

      <div className="campo">
        <label htmlFor="regimenFiscal">Régimen fiscal</label>
        <input
          id="regimenFiscal" className="mono" value={datos.regimenFiscal} maxLength={4}
          onChange={(e) => setDatos({ ...datos, regimenFiscal: e.target.value })}
        />
        <span className="ayuda">Clave de tres o cuatro dígitos del SAT.</span>
      </div>

      <div className="campo">
        <label htmlFor="direccionFacturacion">Domicilio</label>
        <input
          id="direccionFacturacion" value={datos.direccionFacturacion} maxLength={250}
          onChange={(e) => setDatos({ ...datos, direccionFacturacion: e.target.value })}
          placeholder="Sólo la calle"
        />
        <span className="ayuda">
          El número va aparte: el archivo de timbrado los pide separados.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="numeroExterior">Número exterior</label>
        <input
          id="numeroExterior" className="mono" value={datos.numeroExterior} maxLength={20}
          onChange={(e) => setDatos({ ...datos, numeroExterior: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="numeroInterior">Número interior</label>
        <input
          id="numeroInterior" className="mono" value={datos.numeroInterior} maxLength={20}
          onChange={(e) => setDatos({ ...datos, numeroInterior: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="colonia">Colonia</label>
        <input
          id="colonia" value={datos.colonia} maxLength={120}
          onChange={(e) => setDatos({ ...datos, colonia: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="codigoPostal">Código postal</label>
        <input
          id="codigoPostal" className="mono" value={datos.codigoPostal}
          maxLength={5} inputMode="numeric"
          onChange={(e) => setDatos({ ...datos, codigoPostal: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="ciudad">Ciudad</label>
        <input
          id="ciudad" value={datos.ciudad} maxLength={120}
          onChange={(e) => setDatos({ ...datos, ciudad: e.target.value })}
        />
      </div>

      {/* Un desplegable y no texto libre.

          Karin: «en estados poner un select con la lista de estados de
          mexico para facilitar la selección». Y además de facilitar,
          arregla algo: de este campo sale lo que se imprime en la factura,
          y «Edo. de México», «Estado de México», «MEXICO» y «Méx.» son el
          mismo estado y cuatro cadenas distintas.

          Lo que ya estaba guardado y no cuadra con ninguno de los 32 se
          ofrece aparte en vez de desaparecer: un desplegable que no
          incluya el valor guardado arranca enseñando un hueco y lo borra
          al primer guardado. */}
      <div className="campo">
        <label htmlFor="estadoTexto">Estado</label>
        <select
          id="estadoTexto"
          value={datos.estadoTexto}
          onChange={(e) => setDatos({ ...datos, estadoTexto: e.target.value })}
        >
          <option value="">— sin capturar —</option>
          {ESTADOS_DE_MEXICO.map((e) => <option key={e} value={e}>{e}</option>)}
          {datos.estadoTexto && !elEstadoQueEs(datos.estadoTexto)
            && !ESTADOS_DE_MEXICO.includes(datos.estadoTexto) && (
            <option value={datos.estadoTexto}>
              {datos.estadoTexto} (como estaba capturado)
            </option>
          )}
        </select>
        <span className="ayuda">
          El del domicilio fiscal, que es el que se imprime en la factura. No
          es la región de la operación.
        </span>
      </div>

      <h2>Contacto y condiciones</h2>

      <div className="campo">
        <label htmlFor="contactoPrincipal">Contacto principal</label>
        <input
          id="contactoPrincipal" value={datos.contactoPrincipal} maxLength={150}
          onChange={(e) => setDatos({ ...datos, contactoPrincipal: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="correo">Correo</label>
        <input
          id="correo" type="email" value={datos.correo} maxLength={150}
          onChange={(e) => setDatos({ ...datos, correo: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="telefono">Teléfono</label>
        <input
          id="telefono" value={datos.telefono} maxLength={30}
          onChange={(e) => setDatos({ ...datos, telefono: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="diasCredito">Días de crédito</label>
        <input
          id="diasCredito" type="number" min={0} max={365} value={datos.diasCredito}
          onChange={(e) => setDatos({ ...datos, diasCredito: e.target.value })}
        />
      </div>

      <h2>Condiciones de facturación</h2>
      <div className="aviso">
        Se repiten idénticas en cada factura de este cliente y van al
        archivo de timbrado. Sin ellas la prefactura sale con las etiquetas
        vacías, y el timbrado la rechaza.
      </div>

      <div className="campo">
        <label htmlFor="usoCfdi">Uso de CFDI</label>
        <input
          id="usoCfdi" className="mono" value={datos.usoCfdi} maxLength={4}
          onChange={(e) => setDatos({ ...datos, usoCfdi: e.target.value.toUpperCase() })}
        />
        <span className="ayuda">
          Clave del SAT. G03 es «gastos en general», que es lo habitual en
          servicios de mantenimiento.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="metodoPago">Método de pago</label>
        <select
          id="metodoPago" value={datos.metodoPago}
          onChange={(e) => setDatos({ ...datos, metodoPago: e.target.value })}
        >
          <option value="">Elige…</option>
          <option value="PPD">PPD · pago en parcialidades o diferido</option>
          <option value="PUE">PUE · pago en una sola exhibición</option>
        </select>
        <span className="ayuda">
          Con días de crédito va PPD, porque la factura se emite antes de
          cobrarse. PUE es para quien paga en el momento.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="formaPago">Forma de pago</label>
        <input
          id="formaPago" className="mono" value={datos.formaPago} maxLength={2}
          onChange={(e) => setDatos({ ...datos, formaPago: e.target.value })}
        />
        <span className="ayuda">
          Clave del SAT: 99 «por definir», 03 transferencia, 01 efectivo.
          Con PPD suele ir 99, porque al emitir todavía no se sabe cómo va
          a pagar.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="formaPagoTexto">Forma de pago, escrita</label>
        <input
          id="formaPagoTexto" value={datos.formaPagoTexto} maxLength={60}
          onChange={(e) => setDatos({ ...datos, formaPagoTexto: e.target.value })}
          placeholder="Por Definir"
        />
        <span className="ayuda">El archivo de timbrado la pide también con letra.</span>
      </div>

      <div className="campo">
        <label htmlFor="metodoPagoEdi">Método de pago del cliente</label>
        <input
          id="metodoPagoEdi" value={datos.metodoPagoEdi} maxLength={60}
          onChange={(e) => setDatos({ ...datos, metodoPagoEdi: e.target.value })}
        />
        <span className="ayuda">
          No es del catálogo del SAT: cada cliente lo pide con sus propias
          palabras. Si no sabes qué poner, déjalo vacío y pregúntaselo.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="condicionesPago">Condiciones de pago</label>
        <input
          id="condicionesPago" value={datos.condicionesPago} maxLength={150}
          onChange={(e) => setDatos({ ...datos, condicionesPago: e.target.value })}
          placeholder="CREDITO"
        />
      </div>

      <div className="campo">
        <label htmlFor="numeroProveedor">Número de proveedor</label>
        <input
          id="numeroProveedor" className="mono" value={datos.numeroProveedor} maxLength={50}
          onChange={(e) => setDatos({ ...datos, numeroProveedor: e.target.value })}
        />
        <span className="ayuda">
          El número con el que este cliente tiene registrada a APRISA. Lo
          asigna él y va en el archivo de timbrado.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="moneda">Moneda</label>
        <input
          id="moneda" className="mono" value={datos.moneda} maxLength={3}
          onChange={(e) => setDatos({ ...datos, moneda: e.target.value.toUpperCase() })}
        />
      </div>
    </Dialogo>
  );
}

function CambioEstadoCliente({
  cliente, onCerrar, onHecho,
}: {
  cliente: Cliente;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      const accion = cliente.activo ? 'desactivar' : 'reactivar';
      await api.post(`/clientes/${cliente.id}/${accion}`, { motivo });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <DialogoMotivo
      titulo={`${cliente.activo ? 'Desactivar' : 'Reactivar'} cliente`}
      textoBoton={cliente.activo ? 'Desactivar' : 'Reactivar'}
      motivo={motivo}
      setMotivo={setMotivo}
      trabajando={trabajando}
      error={error}
      onConfirmar={confirmar}
      onCerrar={onCerrar}
      descripcion={
        <>
          <p><strong>{cliente.codigo} — {cliente.nombre}</strong></p>
          <div className="aviso" style={{ marginTop: 10 }}>
            {cliente.activo ? 'Se desactivarán también sus ' : 'Se reactivarán también sus '}
            <strong>{cliente.sucursales ?? 0} sucursales</strong>: una sucursal
            de un cliente inactivo no debe seguir ofreciéndose al crear órdenes.
          </div>
        </>
      }
    />
  );
}

function Dato({ etiqueta, valor, mono }: { etiqueta: string; valor: string | null; mono?: boolean }) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      <span className={mono ? 'mono' : undefined}>{valor ?? '—'}</span>
    </div>
  );
}
